import { useState } from 'react';
import { ArrowUpRight, Menu, Moon, Sun, User, X } from 'lucide-react';
import LanguageSelector from './LanguageSelector';

interface GlobalNavbarProps {
  currentView: 'landing' | 'blog';
  theme: 'light' | 'dark';
  t: (key: any) => string;
  onSection: (section: 'features' | 'pricing' | 'faq') => void;
  onBlog: () => void;
  onAuth: (mode: 'login' | 'register') => void;
  onTheme: () => void;
  onHome: () => void;
}

/** Canonical public navbar. Shared by Landing and public Blog SPA routes. */
export default function GlobalNavbar({
  currentView,
  theme,
  t,
  onSection,
  onBlog,
  onAuth,
  onTheme,
  onHome,
}: GlobalNavbarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const go = (section: 'features' | 'pricing' | 'faq') => {
    setMobileOpen(false);
    onSection(section);
  };

  return (
    <>
      <header className="app-navbar landing-navbar" style={{
        position: 'fixed', top: 0, left: 0, right: 0, width: '100%', zIndex: 100,
        background: 'var(--header-bg)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid var(--md-sys-color-outline-variant)', padding: '0 clamp(14px, 2vw, 32px)',
        height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px',
      }}>
        <div className="app-navbar-brand" style={{ display: 'flex', alignItems: 'center', gap: 'clamp(8px, 1.5vw, 16px)' }}>
          <button type="button" className="global-navbar-brand" onClick={onHome} aria-label="Vectria - Go to Home">
            <img src="/emblem.webp" alt="Vectria Emblem" width="30" height="30" />
            <span className="brand-wordmark">VECTRIA</span>
          </button>
          <nav className="landing-nav-links" aria-label="Primary navigation">
            <button type="button" onClick={() => go('features')}>{t('nav.features')}</button>
            <button type="button" onClick={() => go('pricing')}>{t('nav.pricing')}</button>
            <button type="button" onClick={() => go('faq')}>{t('nav.faq')}</button>
            <button type="button" className={currentView === 'blog' ? 'active' : ''} onClick={onBlog}>Blog</button>
          </nav>
        </div>
        <div className="app-navbar-controls" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button type="button" className="m3-btn m3-btn-tonal m3-btn-sm landing-nav-signin" onClick={() => onAuth('login')}>
            <User size={13} /><span>{t('nav.signIn')}</span>
          </button>
          <button type="button" className="m3-btn m3-btn-filled m3-btn-sm landing-nav-register" onClick={() => onAuth('register')}>
            <span>{t('nav.register')}</span><ArrowUpRight size={15} />
          </button>
          <button type="button" className="m3-btn-icon mobile-menu-trigger" onClick={() => setMobileOpen(v => !v)} aria-label="Toggle navigation menu" aria-expanded={mobileOpen}>
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <div className="landing-navbar-language"><LanguageSelector /></div>
          <button id="ssr-theme-toggle" type="button" className="m3-btn-icon landing-navbar-theme" onClick={onTheme} title={t('nav.toggleTheme') || 'Toggle theme'} aria-label={t('nav.toggleTheme') || 'Toggle theme'} aria-pressed={theme === 'light'}>
            {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
          </button>
        </div>
      </header>
      {mobileOpen && (
        <div className="mobile-nav-drawer-backdrop" onClick={() => setMobileOpen(false)}>
          <nav className="mobile-nav-drawer" onClick={event => event.stopPropagation()} aria-label="Mobile navigation">
            <button type="button" onClick={() => go('features')}>{t('nav.features')}</button>
            <button type="button" onClick={() => go('pricing')}>{t('nav.pricing')}</button>
            <button type="button" onClick={() => go('faq')}>{t('nav.faq')}</button>
            <button type="button" onClick={() => { setMobileOpen(false); onBlog(); }}>Blog</button>
          </nav>
        </div>
      )}
    </>
  );
}
